import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import {
  useEmpleadosApi,
  EmpleadoItem,
  EmpleadoDto,
} from "../../../services/api/empleadosApi";
import EmpleadoDataTable from "../../../components/module/admin/GenericDataTable/empleados/EmpleadoDataTable";
import EmpleadoModal from "../../../components/module/admin/EmpleadoModal/EmpleadoModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Empleados = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<EmpleadoItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EmpleadoItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useEmpleadosApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 40 },
    { field: "NumeroDocumento", headerName: "Documento", flex: 1, minWidth: 120 },
    { field: "Nombre", headerName: "Nombres", flex: 1, minWidth: 150 },
    { field: "Apellidos", headerName: "Apellidos", flex: 1, minWidth: 150 },
    {
      field: "cargo",
      headerName: "Cargo",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.cargo?.Nombre || "",
    },
    {
      field: "sucursal",
      headerName: "Sucursal",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.sucursal?.Nombre || "",
    },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton onClick={() => handleView(params.row.Id)} color="info" size="small">
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const data = await getAll();
      if (Array.isArray(data)) {
        setItems(data);
      }
    } catch (error) {
      console.error("Error loading items:", error);
      toast.error("No se pudieron cargar los empleados");
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: EmpleadoItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleView = (id: number) => {
    navigate(`/administracion/empleados/view/${id}`);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción desactivará al empleado.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        await remove(id);
        await loadItems();
        toast.success("El empleado ha sido eliminado correctamente.");
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el empleado.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} este empleado?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: EmpleadoDto = {
            IdEPS: item.IdEPS,
            IdARL: item.IdARL,
            IdAFP: item.IdAFP,
            IdCargo: item.IdCargo,
            IdSucursal: item.IdSucursal,
            IdTipoDocumento: item.IdTipoDocumento,
            IdGenero: item.IdGenero,
            NumeroDocumento: item.NumeroDocumento,
            Nombre: item.Nombre,
            Apellidos: item.Apellidos,
            Telefono: item.Telefono,
            Correo: item.Correo,
            Direccion: item.Direccion,
            FechaIngreso: item.FechaIngreso,
            Imagen: item.Imagen,
            Activo: active,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado del empleado ha sido actualizado.");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        toast.error("No se pudo actualizar el estado.");
      }
    }
  };

  const handleModalSubmit = async (data: EmpleadoDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      await loadItems();
      toast.success("Empleado guardado correctamente");
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Error al guardar el empleado";
      throw new Error(errorMessage);
    }
  };

  return (
    <PageLayout icon={PeopleIcon} title="Empleados">
        <div style={{ padding: "24px" }}>
          <EmpleadoDataTable
            items={items}
            columns={columns}           
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            onView={handleView}
            isLoading={isLoading}
          />
          <EmpleadoModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Empleado" : "Nuevo Empleado"}
            initialData={
              editingItem
                ? {
                    IdEPS: editingItem.IdEPS,
                    IdARL: editingItem.IdARL,
                    IdAFP: editingItem.IdAFP,
                    IdCargo: editingItem.IdCargo,
                    IdSucursal: editingItem.IdSucursal,
                    IdTipoDocumento: editingItem.IdTipoDocumento,
                    IdGenero: editingItem.IdGenero,
                    NumeroDocumento: editingItem.NumeroDocumento,
                    Nombre: editingItem.Nombre,
                    Apellidos: editingItem.Apellidos,
                    Telefono: editingItem.Telefono,
                    Correo: editingItem.Correo,
                    Direccion: editingItem.Direccion,
                    FechaIngreso: editingItem.FechaIngreso,
                    Imagen: editingItem.Imagen,
                    Activo: editingItem.Activo,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Empleados;